import type { GameView, PublicPlayer } from "@coup/shared";
import { Avatar } from "../../components/Avatar";
import { CardBack, CardFace } from "../../components/Card";
import { TimerBar } from "../../components/TimerBar";
import type { Names } from "./names";
import styles from "./Table.module.css";

interface Props {
  player: PublicPlayer;
  view: GameView;
  names: Names;
  x: number;
  y: number;
  /** Total response window, for the small timer bar. */
  responseMs: number;
  selected: boolean;
  onPick?: () => void;
}

export function Seat({ player, view, names, x, y, responseMs, selected, onPick }: Props) {
  const me = player.id === view.you;
  const out = player.cards === 0;
  const active = view.turn.playerId === player.id && view.phase.t !== "over";
  const seat = names.seat(player.id);
  const p = view.phase;
  const waiting =
    p.t === "window" && p.eligible.includes(player.id) && !p.allowed.includes(player.id);
  const allowed = p.t === "window" && p.allowed.includes(player.id);

  return (
    <div
      className={`${styles.seat} ${out ? styles.out : ""}`}
      style={{ left: x, top: y, flexDirection: y >= 450 ? "column" : "column-reverse" }}
      data-seat={player.id}
    >
      <div className={styles.seatCards} style={{ minHeight: me ? 100 : 58 }}>
        {me
          ? view.hand.map((r, i) => <CardFace key={i} role={r} size="sm" />)
          : Array.from({ length: player.cards }, (_, i) => <CardBack key={i} />)}
      </div>
      {!out && (
        <div className={styles.seatCoins} data-coins={player.id}>
          <div className={styles.coinDot} />
          {player.coins}
        </div>
      )}
      <button
        type="button"
        className={styles.plate}
        aria-label={onPick ? `Act on ${seat?.name}` : seat?.name}
        style={{
          border: active
            ? "2px solid var(--timer)"
            : selected
              ? "2px solid var(--gold)"
              : "1px solid var(--plate-line)",
          boxShadow: active
            ? "0 0 0 4px rgba(242,194,48,0.22), 0 0 26px rgba(242,194,48,0.35)"
            : "none",
          cursor: onPick ? "pointer" : "default",
        }}
        onClick={onPick}
      >
        <Avatar avatar={names.avatar(player.id)} size={38} ring={3} />
        {seat?.name}
        {out && <span className={styles.outTag}>{seat?.left ? "left" : "exiled"}</span>}
      </button>
      {seat && !seat.connected && !seat.left && <div className={styles.offline}>Disconnected</div>}
      {waiting && p.t === "window" && (
        <TimerBar deadline={p.deadline} total={responseMs} width={110} height={6} />
      )}
      {allowed && <div className={styles.allowed}>✓ Allowed</div>}
    </div>
  );
}
